export type Sortable = {
  id: string;
  title: string | null;
  lastAt: string | null;
  pinnedAt: string | null;
  unread: number;
  muted: boolean;

  snoozedUntil: string | null;
  archived: boolean;
};

export type FilterName = "all" | "unread" | "archived" | "pinned" | "snoozed" | "muted";

export const THE_CHIPS: readonly FilterName[] = ["all", "unread", "archived"];

export const THE_REST: readonly FilterName[] = ["pinned", "snoozed", "muted"];

export function isFlat(filter: FilterName): boolean {
  return filter === "archived" || filter === "pinned" || filter === "snoozed" || filter === "muted";
}

export function inState<T extends Sortable>(one: T, filter: FilterName): boolean {
  switch (filter) {
    case "all":
      return !one.archived;
    case "unread":
      return one.unread > 0 && !one.archived;
    case "archived":
      return one.archived;
    case "pinned":
      return one.pinnedAt !== null && !one.archived;
    case "snoozed":
      return one.snoozedUntil !== null && !one.archived;
    case "muted":
      return one.muted && !one.archived;
  }
}

export function countsOf<T extends Sortable>(all: readonly T[]): Record<FilterName, number> {
  const counted = (filter: FilterName) => all.filter((one) => inState(one, filter)).length;
  return {
    all: counted("all"),
    unread: counted("unread"),
    archived: counted("archived"),
    pinned: counted("pinned"),
    snoozed: counted("snoozed"),
    muted: counted("muted"),
  };
}

const when = (at: string | null): number => (at === null ? 0 : new Date(at).getTime());

export function inOrder<T extends Sortable>(list: readonly T[], unreadFirst = false): T[] {
  const rank = (one: T): number => {
    if (one.pinnedAt !== null) return 0;
    return unreadFirst && one.unread > 0 ? 1 : 2;
  };
  return [...list].sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;

    if (a.pinnedAt !== null && b.pinnedAt !== null) {
      const byPin = when(a.pinnedAt) - when(b.pinnedAt);
      if (byPin !== 0) return byPin;
    }
    const byActivity = when(b.lastAt) - when(a.lastAt);
    if (byActivity !== 0) return byActivity;

    return a.id < b.id ? 1 : a.id > b.id ? -1 : 0;
  });
}

export function titleMatches(title: string, typed: string): boolean {
  const wanted = typed.trim().toLowerCase();
  if (wanted === "") return true;
  return title.toLowerCase().includes(wanted);
}
